import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Clock, Users, Navigation, CheckCircle2 } from 'lucide-react';
import { useMetadata } from '../hooks/useMetadata';

export default function About() {
  const [cmsPage, setCmsPage] = useState(null);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/by-slug/da/om-os')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente om os fra CMS');
        return res.json();
      })
      .then((data) => {
        if (data && data.collectionName === 'pages') {
          setCmsPage(data);
        }
      })
      .catch((err) => console.error(err));
  }, []);

  // Brug metadata fra CMS eller fallback
  const title = cmsPage?.meta_title || 'Om os - FLOW Transport & Logistik ApS';
  const description =
    cmsPage?.meta_description ||
    'Læs mere om FLOW Transport & Logistik ApS. Vi er et dedikeret team af transportprofessionelle med fokus på pålidelighed og høj service.';

  useMetadata(title, description);

  return (
    <div className="w-full bg-white font-sans">
      {/* Side Header */}
      <section className="bg-brand-dark text-white py-16 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(#0056b3_1px,transparent_1px)] [background-size:16px_16px]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-serif font-bold tracking-tight">
            Om FLOW Transport & Logistik ApS
          </h1>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto font-sans">
            Lær os bedre at kende. Vi er et dedikeret team af transportprofessionelle, der sætter en ære i at levere dine varer sikkert og til tiden.
          </p>
        </div>
      </section>

      {/* Præsentation & Historie */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Venstre: Tekst */}
            <div className="lg:col-span-7 space-y-6">
              <div className="text-sm font-bold text-brand-primary uppercase tracking-wider">
                Vores historie & vision
              </div>
              <h2 className="text-3xl font-serif font-bold text-brand-primary tracking-tight">
                En pålidelig partner bygget på tillid
              </h2>
              <div className="text-slate-600 leading-relaxed space-y-4 text-sm sm:text-base">
                <p>
                  FLOW Transport & Logistik er en dansk transportvirksomhed med fokus på pålidelige og fleksible transportløsninger. Vi hjælper både virksomheder og private med sikker og effektiv transport - fra mindre leveringer til større logistiske opgaver.
                </p>
                <p>
                  Hos FLOW lægger vi vægt på kvalitet, punktlighed og god service.
                </p>
                <p>
                  Vores mål er at sikre, at dine varer når frem til tiden og i god stand.
                </p>
                <p>
                  Med erfaring inden for kurérkørsel, flytning, godstransport og logistik tilbyder vi løsninger, der er tilpasset dine behov.
                </p>
                <p className="font-medium text-brand-dark pt-2 border-t border-slate-100">
                  Vi arbejder hver dag for at gøre transport enkelt og effektiv for vores kunder - fra flyttekasse til fuld logistik.
                </p>
              </div>
            </div>

            {/* Højre: Fakta & Hvorfor vælge os */}
            <div className="lg:col-span-5">
              <div className="bg-slate-50 border border-slate-100 rounded-2xl p-8 shadow-sm space-y-6">
                <h3 className="text-xl font-serif font-bold text-brand-dark">
                  Hvorfor vælge FLOW?
                </h3>
                <p className="text-slate-600 text-sm">
                  Vi kombinerer personlig service med professionel logistikplanlægning for at give dig den bedste oplevelse.
                </p>
                
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="p-1 rounded bg-brand-primary/10 text-brand-primary shrink-0 mt-0.5">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-brand-dark">100% forsikret transport</h4>
                      <p className="text-xs text-slate-500">Dit gods er fuldt dækket under hele transporten.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="p-1 rounded bg-brand-primary/10 text-brand-primary shrink-0 mt-0.5">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-brand-dark">Landsdækkende netværk</h4>
                      <p className="text-xs text-slate-500">Vi leverer i hele landet med kort varsel.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="p-1 rounded bg-brand-primary/10 text-brand-primary shrink-0 mt-0.5">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-brand-dark">Personlig kontaktperson</h4>
                      <p className="text-xs text-slate-500">Du taler altid direkte med en, der kender din opgave.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="p-1 rounded bg-brand-primary/10 text-brand-primary shrink-0 mt-0.5">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-brand-dark">Hurtig tilbudsgivning</h4>
                      <p className="text-xs text-slate-500">Få et uforpligtende tilbud tilpasset dine behov.</p>
                    </div>
                  </li>
                </ul>

                <div className="pt-4 border-t border-slate-200/60">
                  <Link
                    to="/kontakt"
                    className="w-full inline-flex items-center justify-center bg-brand-primary hover:bg-brand-primary/95 text-white py-3 px-4 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors shadow-sm cursor-pointer"
                  >
                    Få et uforpligtende tilbud
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Værdier Sektion */}
      <section className="py-20 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="text-sm font-bold text-brand-primary uppercase tracking-wider mb-2">
              Vores værdigrundlag
            </div>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-brand-primary tracking-tight">
              De fire søjler i vores arbejde
            </h2>
            <p className="mt-4 text-slate-600 text-sm sm:text-base">
              Vores værdier er ikke bare ord på et papir — de styrer vores beslutninger og handlinger hver eneste dag på landevejen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* Værdi 1 */}
            <div className="bg-white p-8 rounded-2xl shadow-3d-card border border-slate-100 flex gap-5">
              <div className="p-3 rounded-lg bg-brand-primary/10 text-brand-primary shrink-0 h-12 w-12 flex items-center justify-center">
                <Shield className="h-6 w-6" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-brand-dark">Pålidelighed og sikkerhed</h3>
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                  Vi passer på dit gods, som var det vores eget. Vores biler er fuldt forsikrede, og vores chauffører er uddannet i sikker godshåndtering, så dine varer altid kommer frem i perfekt stand.
                </p>
              </div>
            </div>

            {/* Værdi 2 */}
            <div className="bg-white p-8 rounded-2xl shadow-3d-card border border-slate-100 flex gap-5">
              <div className="p-3 rounded-lg bg-brand-primary/10 text-brand-primary shrink-0 h-12 w-12 flex items-center justify-center">
                <Clock className="h-6 w-6" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-brand-dark">Punktlighed og præcision</h3>
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                  En aftale er en aftale. Vi forstår, at forsinkelser kan have store konsekvenser for din forretning. Derfor planlægger vi grundigt og leverer altid inden for det aftalte tidsvindue.
                </p>
              </div>
            </div>

            {/* Værdi 3 */}
            <div className="bg-white p-8 rounded-2xl shadow-3d-card border border-slate-100 flex gap-5">
              <div className="p-3 rounded-lg bg-brand-primary/10 text-brand-primary shrink-0 h-12 w-12 flex items-center justify-center">
                <Users className="h-6 w-6" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-brand-dark">Personlig service</h3>
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                  Hos os er du ikke bare et kundenummer. Vi tror på den personlige relation, tæt dialog og hurtig respons. Du får altid fat i en medarbejder, der kender din sag og kan træffe hurtige beslutninger.
                </p>
              </div>
            </div>

            {/* Værdi 4 */}
            <div className="bg-white p-8 rounded-2xl shadow-3d-card border border-slate-100 flex gap-5">
              <div className="p-3 rounded-lg bg-brand-primary/10 text-brand-primary shrink-0 h-12 w-12 flex items-center justify-center">
                <Navigation className="h-6 w-6" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-brand-dark">Effektiv planlægning</h3>
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                  Vi anvender moderne ruteplanlægningssystemer for at minimere tomkørsel og optimere vores ruter. Det sikrer hurtig levering, konkurrencedygtige priser og en mere miljøvenlig transport.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Kontakt CTA */}
      <section className="bg-brand-dark text-white py-16 text-center relative overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <h2 className="text-3xl font-serif font-bold tracking-tight">
            Vil du høre mere om et samarbejde?
          </h2>
          <p className="text-slate-300 text-sm sm:text-base max-w-2xl mx-auto">
            Uanset om du har brug for en fast aftale eller blot en enkeltstående transport, står vi klar til at rådgive dig om den bedste løsning.
          </p>
          <div className="pt-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center bg-brand-primary hover:bg-brand-primary/95 text-white px-8 py-4 rounded-lg text-sm font-bold uppercase tracking-wider transition-colors shadow-lg cursor-pointer"
            >
              Kontakt os i dag
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
